<template>
  <div>
    <ProgressBar
      v-if="!isLiteView()"
      :active-step="pageTitle"
    />
    <PageHeading
      :default-heading="pageTitle"
      :edit-heading="pageTitle"
    />
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          What's their name and role?
        </h2>
        <apx-full-name
          id="fullName"
          group-label="Name"
          :first-name="firstName"
          first-name-label="First"
          :middle-name="middleName"
          middle-name-label="Middle"
          :last-name="lastName"
          last-name-label="Last"
          required
          @value-changed="fullNameChanged"
          :readonly = "trustedRiskDone"
        />
        <div class="pvd-form-row">
          <div class="form-row-label form-row-label-top label-icon">
            <label id="trust-role--label">
              <a
                id="trust-role-modal-trigger"
                href="javascript:void(0);"
              >
                <pvd-icon
                  icon="help"
                  class="m-small-left"
                />
              </a> Role in trust</label>
          </div>
          <pvd-modal
            id="trust-role-modal"
            for="trust-role-modal-trigger"
            class="small"
          >
            <div slot="modal-header">
              <h2 class="pvd-heading-L">
                Trust roles
              </h2>
            </div>
            <div slot="modal-body">
              <div class="pvd-container">
                <p>
                  <strong>Trustee</strong><br>
                  The trustee is a person or institution who has control of a trust’s assets and has a fiduciary
                  responsibility to appropriately handle the assets. A Fidelity trust account needs at least one
                  trustee, with a maximum number of 4.
                </p>
                <p>
                  <strong>Grantor</strong><br>
                  A grantor is the person who creates or contributes the assets to a trust. A Fidelity trust account
                  needs at least one grantor, with a maximum of 6.
                </p>
              </div>
            </div>
          </pvd-modal>
          <div class="form-row-content">
            <fieldset aria-labelledby="trust-role--label">
              <div class="pvd-radio-btn">
                <input
                  id="trust-additional-role--trustee"
                  v-model="trustRoleValue"
                  v-validate="{required: true}"
                  value="trustee"
                  type="radio"
                  name="trust-additional-role"
                  @click="trusteeClicked"
                >
                <label for="trust-additional-role--trustee">Trustee</label>
              </div>
              <div class="pvd-radio-btn">
                <input
                  id="trust-additional-role--trustee-grantor"
                  v-model="trustRoleValue"
                  value="trustee/grantor"
                  type="radio"
                  name="trust-additional-role"
                  @click="trusteeGrantorClicked"
                >
                <label for="trust-additional-role--trustee-grantor">Trustee and grantor</label>
              </div>
              <div class="pvd-radio-btn">
                <input
                  id="trust-additional-role--grantor"
                  v-model="trustRoleValue"
                  value="grantor"
                  type="radio"
                  name="trust-additional-role"
                  @click="grantorClicked"
                >
                <label for="trust-additional-role--grantor">Grantor</label>
              </div>
            </fieldset>
          </div>
          <InlineMessage
            v-if="errors.has('trust-additional-role')"
            message="Please make a selection."
            css-class="form-row-message p-small-top"
          />
          <InlineMessage
            v-if="hasMaxTrustees"
            id="trusteeError"
            message="You’ve reached the limit of 4 trustees for this account.
            To replace a trustee, you'll have to edit one you previously added."
            css-class="form-row-message"
          />
          <InlineMessage
            v-if="hasMaxGrantors"
            id="grantorError"
            message="You’ve reached the limit of 6 grantors for this account.
            To replace a grantor, you'll have to edit one you previously added."
            :css-class="hasMaxTrustees ? 'form-row-message mt-1-5' : 'form-row-message'"
          />
        </div>
        <ToggleComponent
          v-if="trustRole == 'grantor'"
          id-prefix="trust-additional"
          :toggle-value="isGrantorLiving"
          yes-label="Yes"
          no-label="No"
          label="Is the grantor living?"
          :track-events="true"
          :validation-rules="selectValidation"
          message="Please make a selection."
          show-internal-error-message="true"
          @update:toggleValue="updateGrantorLiving"
        />
      </div>
      <div
        v-if="!hasMaxGrantors && !hasMaxTrustees"
        :key="rerenderKey"
      >
        <div
          v-if="showAuthorities"
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            Select any authorities granted by the trust agreement
          </h2>
          <div
            class="pvd-form-row"
            role="group"
            aria-labelledby="checkgroup-label"
          >
            <div class="form-row-label form-row-label-top label-icon">
              <label
                id="checkgroup-label"
                class="pvd-label"
              >
                <a
                  id="trust-authorities-modal-trigger"
                  href="javascript:void(0);"
                >
                  <pvd-icon
                    icon="help"
                    class="m-small-left"
                  />
                </a> Authorities</label>
            </div>
            <pvd-modal
              id="trust-authorities-modal"
              for="trust-authorities-modal-trigger"
              class="small"
            >
              <div slot="modal-header">
                <h2 class="pvd-heading-L">
                  Trust authorities
                </h2>
              </div>
              <div slot="modal-body">
                <div class="pvd-container">
                  <p>
                    Trust authorities include the power to amend conditions of the trust,
                    revoke conditions of the trust, and the power to appoint or remove
                    individual trustees. The people who may hold these authorities can
                    vary by type of trust and the specific trust terms.
                    <br>
                    <br>
                    <strong>Refer to your trust documents</strong>
                    for this information when answering this question.
                  </p>
                </div>
              </div>
            </pvd-modal>
            <div class="form-row-content">
              <fieldset aria-labelledby="trust-role--label">
                <div class="pvd-checkbox">
                  <input
                    id="checkbox-form1"
                    v-model="amendValue"
                    type="checkbox"
                    name="checkbox-form"
                    value="amend conditions"
                  >
                  <label for="checkbox-form1">
                    <span
                      class="checkmark"
                      aria-hidden="true"
                    >
                      <pvd-icon icon="checkmark" />
                    </span>
                    Can amend conditions of the trust
                  </label>
                </div>
                <div
                  v-if="acc_details.isIrrevocable === false"
                  class="pvd-checkbox"
                >
                  <input
                    id="checkbox-form3"
                    v-model="revokeValue"
                    type="checkbox"
                    name="checkbox-form"
                    value="revoke conditions"
                  >
                  <label for="checkbox-form3">
                    <span
                      class="checkmark"
                      aria-hidden="true"
                    >
                      <pvd-icon icon="checkmark" />
                    </span>
                    Can revoke conditions of the trust
                  </label>
                </div>
                <div class="pvd-checkbox">
                  <input
                    id="checkbox-form2"
                    v-model="appointRemoveValue"
                    type="checkbox"
                    name="checkbox-form"
                    value="appoint/remove trustees"
                  >
                  <label for="checkbox-form2">
                    <span
                      class="checkmark"
                      aria-hidden="true"
                    >
                      <pvd-icon icon="checkmark" />
                    </span>
                    Can appoint/remove trustees
                  </label>
                </div>
              </fieldset>
            </div>
            <InlineMessage
              v-show="!appointRemoveValid && amendRevokeValid"
              message="You've reached the limit of 6 people with appoint/remove authority. To replace a person with this authority,
              you'll have to edit one you previously added."
              css-class="form-row-message p-small-top"
            />
            <InlineMessage
              v-show="!amendRevokeValid && appointRemoveValid"
              message="You've reached the limit of 6 people with amend/revoke authority. To replace a person with this authority,
              you'll have to edit one you previously added."
              css-class="form-row-message p-small-top"
            />
            <InlineMessage
              v-show="!amendRevokeValid && !appointRemoveValid"
              message="You've reached the limit of 6 people with amend/revoke authority and 6 people with appoint/remove authority.
              To replace a person with these authorities, you'll have to edit one you previously added."
              css-class="form-row-message p-small-top"
            />
          </div>
        </div>
        <div
          v-if="showAdditionalInfo"
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            Enter their personal information details
          </h2>
          <apx-ssn
            id="ssn"
            class="additional-ssn"
            label="Social Security number"
            :value="ssn"
            :validation-rules="buildTrustSSNRules()"
            required
            @value-changed="ssnChanged"
            :readOnly="trustedRiskDone"
          />
          <div class="pvd-form-row top-margin-zero">
            <div class="form-row-label">
              &nbsp;
            </div>
            <div class="form-row-content">
              &nbsp;
            </div>
            <InlineMessage
              v-if="ssnMatchesExistingSSN"
              message="This SSN has already been entered for someone else.
                Please enter a valid SSN for this person, or review and correct
                the entries you have already made."
              css-class="form-row-message p-small-top"
            />
          </div>
          <div class="pvd-form-row top-margin-zero dob-margin"
            v-if = "trustedRiskDone"
          >
            <div class="form-row-label dob-coc">
             DOB &nbsp;
            </div>
            <div class="form-row-content">
              {{ dobDay }}/{{ dobMonth }}/{{ dobYear }}
            </div>
          </div>
          <apx-date-select
            v-else
            id="dateOfBirth"
            group-label="Date of birth"
            :day-value="dobDay"
            :month-value="dobMonth"
            :year-value="dobYear"
            validation-text="The date of birth entered is not a valid date."
            default-select-label="Select"
            min-date="01/01/1900"
            eighteen-or-above
            eighteen-or-above-error="This person must be at least 18 years old."
            required
            @value-changed="dateOfBirthChanged"
            :readOnly="trustedRiskDone"
          />
          <div class="pvd-form-row top-margin-zero"
            v-if = "trustedRiskDone"
          >
            <div class="form-row-label dob-coc">
             Country of citizenship &nbsp;
            </div>
            <div class="form-row-content">
              {{ selectedCitizenshipValue() }}
            </div>
          </div>
          <apx-select
            v-if="showCountrySelect && !trustedRiskDone"
            id="residenceCountryList"
            label="Country of citizenship"
            error-text="Select a country of citizenship."
            :options="countryList"
            :value="residenceCountry"
            required
            @value-changed="residenceCountryChanged"
          />
          <apx-phone
            v-if="trustRole != 'grantor'"
            id="phoneNumber"
            telephone-number-label="Mobile number"
            :telephone-number="phoneNumber"
            required
            @value-changed="phoneNumberChanged"
            :readOnly="trustedRiskDone"
          />
          <apx-email
            v-if="trustRole != 'grantor'"
            id="email"
            label="Email"
            :value="email"
            required
            @value-changed="emailChanged"
            :readOnly="trustedRiskDone"
          />
          <div class="pvd-form-row top-margin-zero">
            <div class="form-row-label">
              &nbsp;
            </div>
            <div class="form-row-content">
              &nbsp;
            </div>
            <InlineMessage
              v-if="emailMatchesExistingTrusteesEmail"
              message="You’ve already entered this email for someone else.
              Please enter a different email or change this email for your previous entry."
              css-class="form-row-message p-small-top"
            />
          </div>
        </div>
      </div>
      <div
        v-if="!hasMaxGrantors && !hasMaxTrustees"
        :key="rerenderKey+1"
      >
        <div
          v-if="showLegalAddress"
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            What's their legal residential address?
          </h2>
          <apx-address
            id="legalAddress"
            id-prefix="additionalLegalAddress"
            :address-line1="legalAddress.line1"
            :address-line2="legalAddress.line2"
            :city-name="legalAddress.city"
            :state-code="legalAddress.state"
            :zip-code="legalAddress.zip"
            prevent-pobox
            required
            @value-changed="addressChanged('legal', $event)"
          />
          <div
            class="pvd-form-row"
            role="group"
          >
            <div class="form-row-label form-row-label-top">
              <span
                class="pvd-label"
              >
                &nbsp;
              </span>
            </div>
            <div class="form-row-content">
              <fieldset>
                <div class="pvd-checkbox">
                  <input
                    id="additionalIsMailingSameAsLegal"
                    v-model="isMailingSameAsLegalValue"
                    type="checkbox"
                    name="checkbox-form"
                    value="true"
                  >
                  <label for="additionalIsMailingSameAsLegal">
                    <span
                      class="checkmark"
                      aria-hidden="true"
                    >
                      <pvd-icon icon="checkmark" />
                    </span>
                    The mailing address is the same as the legal address
                  </label>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div
          v-if="showMailingAddress"
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            {{ mailingAddressTitle }}
          </h2>
          <apx-address
            id="mailingAddress"
            id-prefix="additionalMailingAddress"
            :address-line1="mailingAddress.line1"
            :address-line2="mailingAddress.line2"
            :city-name="mailingAddress.city"
            :state-code="mailingAddress.state"
            :zip-code="mailingAddress.zip"
            prevent-pobox
            required
            @value-changed="addressChanged('mailing', $event)"
          />
        </div>

        <div
          v-if="trustRole == 'trustee' || trustRole == 'trustee/grantor'"
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            Are they employed?
          </h2>
          <apx-select
            id="employmentStatus"
            label="Employment status"
            :value="employmentStatus"
            error-text="Industry regulations require Fidelity Investments to obtain your employment status."
            :options="options"
            required
            @change="employmentStatusChanged"
          />
        </div>

        <div
          v-if="showEmploymentInformation"
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            Employment information
          </h2>
          <div class="pvd-form-row">
            <div class="form-row-label">
              <label for="occupation">Occupation</label>
            </div>
            <div class="form-row-content">
              <input
                id="occupation"
                v-model="occupation"
                v-validate="{required: showEmploymentInformation}"
                name="occupation"
                type="text"
                class="pvd-input"
                minlength="1"
                maxlength="32"
                @change="occupationChanged"
              >
            </div>
            <InlineMessage
              v-if="errors.has('occupation')"
              message="Provide their occupation."
              css-class="form-row-message p-small-top"
            />
          </div>
          <div class="pvd-form-row">
            <div class="form-row-label">
              <label for="company">Company name</label>
            </div>
            <div class="form-row-content">
              <input
                id="company"
                v-model="company"
                v-validate="{required: showEmploymentInformation}"
                name="company"
                type="text"
                class="pvd-input"
                minlength="1"
                maxlength="50"
                @change="companyNameChanged"
              >
            </div>
            <InlineMessage
              v-if="errors.has('company')"
              message="Provide your company's name."
              css-class="form-row-message p-small-top"
            />
          </div>
          <div
            v-if="employmentStatus === 'S'"
            id="seCompanyAddressSection"
          >
            <ToggleComponent
              v-if="employmentStatus === 'S'"
              id-prefix="seAddress"
              :toggle-value="isSECompanyAddressSame"
              yes-label="Yes"
              no-label="No"
              label="Is their company's address the same as their legal address?"
              :track-events="true"
              :validation-rules="selectValidation"
              message="Please make a selection."
              @update:toggleValue="isSECompanyAddressSameChanged"
            />
          </div>
          <apx-address
            v-if="showCompanyAddress && !(isSECompanyAddressSame === 'Yes')"
            id="employmentAddress"
            id-prefix="employmentAddress"
            :address-line1="employerAddress.line1"
            :address-line2="employerAddress.line2"
            :city-name="employerAddress.city"
            :state-code="employerAddress.state"
            :zip-code="employerAddress.zip"
            prevent-pobox
            @value-changed="addressChanged('employer', $event)"
          />
          <apx-address
            v-if="(isSECompanyAddressSame === 'Yes') && (legalAddress.city.length > 0) && (legalAddress.state.length > 0)"
            id="employmentAddressSame"
            id-prefix="employmentAddress"
            :address-labels="JSON.stringify({addressGroup: 'Company adddress'})"
            :address-line1="legalAddress.line1"
            :address-line2="legalAddress.line2"
            :city-name="legalAddress.city"
            :state-code="legalAddress.state"
            :zip-code="legalAddress.zip"
            prevent-pobox
            readonly
          />
        </div>
        <div
          v-if="showAssociations"
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            Associations
          </h2>
          <div
            v-if="employmentStatus !== '' && employmentStatus !== 'Z'"
            class="pvd-form-row"
          >
            <div class="form-row-label-group toggle-button-stacked">
              <label>
                Are you, or an immediate family member residing in the same household, employed by or associated
                with any of the following: a broker-dealer, stock exchange, exchange member firm, the Financial
                Industry Regulatory Authority (FINRA), a municipal securities dealer, or other financial institution? <a
                  id="associations-modal-trigger"
                  href="javascript:void(0);"
                ><pvd-icon
                  icon="help"
                  class="m-small-left"
                /></a>
              </label>
            </div>
            <pvd-modal
              id="associations-modal"
              for="associations-modal-trigger"
              class="small"
            >
              <div slot="modal-header">
                <h2 class="pvd-heading-L">
                  Investment Industry Associations
                </h2>
              </div>
              <div slot="modal-body">
                <div class="pvd-container">
                  <p>
                    As specified by FINRA Rule 3210, to open and maintain an account at Fidelity, a person
                    with any of these associations must obtain prior written consent from his or her employer.
                    Additionally, upon request, Fidelity will report transactional data to your employer.
                  </p>
                </div>
              </div>
            </pvd-modal>
            <ToggleComponent
              :id-prefix="'associationToggle'"
              :toggle-value="hasAssociation"
              yes-label="Yes"
              no-label="No"
              :track-events="true"
              :validation-rules="selectValidation"
              message="Please make a selection."
              :conditional-class="'form-row-content'"
              @update:toggleValue="hasAssociationChanged"
            />
          </div>
          <div
            v-if="showAssociationsInput"
            class="pvd-form-row"
          >
            <div class="form-row-label">
              <label for="association">Association</label>
            </div>
            <div class="form-row-content">
              <input
                id="association"
                v-model="association"
                v-validate="{required: showAssociationsInput}"
                name="association"
                type="text"
                class="pvd-input"
                maxlength="50"
                @change="associationChanged"
              >
            </div><InlineMessage
              v-if="errors.has('association')"
              message="Provide the name of the company with which you are affiliated."
              css-class="form-row-message p-small-top"
            />
            <div class="form-row-supplemental">
              <p class="xsmall">
                By selecting Yes above, you authorize Fidelity to provide
                the associated person's employer with duplicate copies of confirmations and statements,
                or the transactions data contained therein, for any account(s) on a consolidated statement
                for purposes of their compliance review.
              </p>
            </div>
          </div>
          <div class="pvd-form-row">
            <div class="form-row-label-group toggle-button-stacked">
              <label>
                Are you, your spouse, or
                <a
                  id="tt-popover-trigger"
                  href="javascript:void(0);"
                  @click="track('any_of_your_relatives', 'interaction')"
                >
                  any of your relatives
                </a>living in your home (at the same address),
                a member of the board of directors, a 10% shareholder, a policy-making officer,
                or someone who can direct the management and policies of a publicly traded company?
              </label>
            </div>
            <ToggleComponent
              :id-prefix="'controlPersonToggle'"
              :toggle-value="hasControlPersonRelationship"
              yes-label="Yes"
              no-label="No"
              :track-events="true"
              :validation-rules="selectValidation"
              message="Please make a selection."
              :conditional-class="'form-row-content'"
              @update:toggleValue="hasControlPersonRelationshipChanged"
            />
          </div>
          <div
            v-if="showTradingSymbolInput"
            class="pvd-form-row"
          >
            <div class="form-row-label">
              <label for="tradingSymbol">{{ isManagedAccount ? 'Trading Symbol or CUSIP' : 'Trading Symbol' }}</label>
            </div>
            <div class="form-row-content">
              <input
                id="tradingSymbol"
                v-model="tradingSymbol"
                v-validate="{required: true}"
                name="tradingSymbol"
                type="text"
                class="pvd-input"
                maxlength="5"
                @change="tradingSymbolChanged"
              >
            </div>
            <InlineMessage
              v-if="errors.has('tradingSymbol')"
              message="Provide the trading symbol of the publicly held company."
              css-class="form-row-message p-small-top"
            />
          </div>
        </div>
      </div>

      <PageNavigation
          v-if="isOnlyGrantor || trustedRiskDone"
          primary-button-type="button"
          :primary-button-label="activePrimaryButtonLabel"
          :show-primary-button-spinner="primaryButtonLoading"
          :show-secondary-button-spinner="secondaryButtonLoading"
          :secondary-button-label="activeSecondaryButtonLabel"
          @primary-button-clicked="primaryButtonClick"
          @secondary-button-clicked="secondaryButtonClick"
        />
      <NavigationComponent
        v-else
        @next-action="handleWidgetSubmit"
      />
    </form>
    <div
      id="fs-steps"
      class="personal-info-page"
      data-operation="IdentifyUser"
      data-step="Init"
      style="display: none"
    >
      &nbsp;
    </div>
  </div>
</template>

<script>
import ProgressBar from '../components/ProgressBar.vue';
import NavigationComponent from '../components/NavigationComponent.vue';
import PageNavigation from '../components/PageNavigation.vue';
import ToggleComponent from '../components/ToggleComponent.vue';
import PageHeading from '../components/PageHeading.vue';
import dataHelper from '../mixins/dataHelper';
import dataSanitize from '../mixins/dataSanitize';
import measurement from '../mixins/measurement';
import utilityFunctions from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import widgetHelper from '../mixins/widgetHelper';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import trustHelper from '../mixins/trustHelper';
import addressAutocomplete from '../mixins/addressAutocomplete';
import InlineMessage from '../components/InlineMessage.vue';

export default {
  name: 'TrustAdditionalRole',
  components: {
    ProgressBar,
    NavigationComponent,
    ToggleComponent,
    PageHeading,
    InlineMessage,
    PageNavigation,
  },
  mixins: [
    dataHelper,
    dataSanitize,
    measurement,
    utilityFunctions,
    validationHelper,
    widgetHelper,
    pageNavigationHelper,
    trustHelper,
    addressAutocomplete,
  ],
  data() {
    return {
      MAX_TRUSTEES: 4,
      MAX_GRANTORS: 6,
      MAX_APPOINT_REMOVE: 6,
      MAX_AMEND_REVOKE: 6,
      rerenderKey: 0,
      trustRole: '',
      hasAppointRemove: false,
      hasAmendRevoke: false,
      hasAmend: false,
      hasRevoke: false,
      isMailingSameAsLegal: true,
      backButtonAttempt: false,
      primaryButtonLabel: 'Next',
      additionalRoles: [],
      roleLegalAddress: {
        stateCode: '',
      },
      options: '[{"label":"Select one","value":""},{"label":"Employed","value":"E"},{"label":"Not Employed","value":"N"},{"label":"Self Employed","value":"S"},{"label":"Employed by Fidelity Investments","value":"Z"},{"label":"Retired","value":"R"}]',
      employmentStatus: '',
      mailingAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      },
      legalAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      },
      employerAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      },
      ssn: '',
      firstName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: '',
      phoneNumber: '',
      email: '',
      residenceCountry: '',
      mobilePhoneCheckIndicator: true,
      isGrantorLiving: '',
      occupation: '',
      company: '',
      hasAssociation: '',
      association: '',
      hasControlPersonRelationship: '',
      tradingSymbol: '',
      isSECompanyAddressSame: 'Yes',
      additionalUserIndex: 0,
      currentUser: undefined,
      selectValidation: {
        regex: '^(Yes|No)$',
        required: 'true',
      },
      selectedRole: '',
      trust: {},
      applicant: {},
      isLoading: false,
      pageTitle: 'Tell us about the person you\'re adding',
      localEditMode: false,
      futureDestinationState: null,
    };
  },
  computed: {
    shouldDisableNext() {
      return this.isLoading ? 'Y' : 'N';
    },
    shouldShowLoadingSpinner() {
      return this.isLoading ? 'Y' : 'N';
    },
    activePrimaryButtonLabel() {
      return this.isLoading ? this.pd_content.primary_button.wait : this.primaryButtonLabel;
    },
    activeSecondaryButtonLabel() {
      if (this.isLoading) return this.pd_content.secondary_button.wait;
      return this.localEditMode ? this.pd_content.secondary_button.edit : this.pd_content.secondary_button.default;
    },
    ssnMatchesExistingSSN: {
      get() {
        const existingSSNs = [
          ...this.additionalRoles.filter((user, index) => index !== this.additionalUserIndex)
            .map((user) => user.ssn),
          this.applicant.ssn,
        ];
        return this.ssn && existingSSNs.includes(this.ssn);
      },
    },
    emailMatchesExistingTrusteesEmail: {
      get() {
        const { editMode } = this.getInstanceData().currentState;
        const existingEmails = [
          ...this.additionalRoles.filter((user, index) => index !== this.additionalUserIndex && (user.role === 'trustee' || user.role === 'trustee/grantor'))
            .map((user) => user.email),
          this.applicant.email,
        ];
        return !editMode && this.email
        && existingEmails.map((email) => (email ? email.toUpperCase() : email)).includes(this.email.toUpperCase());
      },
    },
    trustRoleValue: {
      get() {
        return this.trustRole;
      },
      set(newTrustRole) {
        this.trustRole = newTrustRole;
      },
    },
    showLegalAddress: {
      get() {
        const show = this.trustRole === 'trustee' || this.trustRole === 'trustee/grantor';
        if (show) {
          this.initGoogleAutocomplete('additionalLegalAddress-addressLine1', this.trustFillAddressValues.bind(null, 'additionalLegalAddress', this.addressChanged.bind(this, 'legal')));
        }
        return show;
      },
    },
    showMailingAddress: {
      get() {
        const show = (this.trustRole !== 'grantor' && this.isMailingSameAsLegal === false) || (this.trustRole === 'grantor' && this.isGrantorLiving === 'Yes');
        if (show) {
          this.initGoogleAutocomplete('additionalMailingAddress-addressLine1', this.trustFillAddressValues.bind(null, 'additionalMailingAddress', this.addressChanged.bind(this, 'mailing')));
        }
        return show;
      },
    },
    mailingAddressTitle: {
      get() {
        if (this.isMailingSameAsLegal === false) {
          return 'What\'s their mailing address?';
        }
        return 'What\'s their legal residential address?';
      },
    },
    isMailingSameAsLegalValue: {
      get() {
        return this.isMailingSameAsLegal;
      },
      set(newIsMailingSameAsLegal) {
        this.isMailingSameAsLegal = newIsMailingSameAsLegal;
        this.commitAdditionalUserData(this.isMailingSameAsLegal, 'isMailingSameAsLegal', this.additionalUserIndex);
      },
    },
    appointRemoveValid() {
      if (this.hasAppointRemove && this.hasMaxAppointRemove) return false;
      return true;
    },
    amendRevokeValid() {
      if (this.hasAmendRevoke && this.hasMaxAmendRevoke) return false;
      return true;
    },
    roleValid() {
      if (this.hasMaxGrantors && (this.selectedRole === 'grantor' || this.selectedRole === 'trustee/grantor')) return false;
      if (this.hasMaxTrustees && (this.selectedRole === 'trustee' || this.selectedRole === 'trustee/grantor')) return false;
      return true;
    },
    appointRemoveValue: {
      get() {
        return this.hasAppointRemove;
      },
      set(newHasAppointRemove) {
        this.hasAppointRemove = newHasAppointRemove;
        this.commitAdditionalUserData(this.hasAppointRemove, 'hasAppointRemove', this.additionalUserIndex);
      },
    },
    amendRevokeValue: {
      get() {
        return this.hasAmendRevoke;
      },
      set(newHasAmendRevoke) {
        this.hasAmendRevoke = newHasAmendRevoke;
        this.commitAdditionalUserData(this.hasAmendRevoke, 'hasAmendRevoke', this.additionalUserIndex);
      },
    },
    amendValue: {
      get() {
        return this.hasAmend;
      },
      set(newHasAmend) {
        this.hasAmend = newHasAmend;
        this.amendRevokeValue = (this.hasAmend || this.hasRevoke);
        this.commitAdditionalUserData(this.hasAmend, 'hasAmend', this.additionalUserIndex);
      },
    },
    revokeValue: {
      get() {
        return this.hasRevoke;
      },
      set(newHasRevoke) {
        this.hasRevoke = newHasRevoke;
        this.amendRevokeValue = (this.hasRevoke || this.hasAmend);
        this.commitAdditionalUserData(this.hasRevoke, 'hasRevoke', this.additionalUserIndex);
      },
    },
    showAuthorities: {
      get() {
        return (this.trustRole === 'grantor' && this.isGrantorLiving === 'Yes') || (this.trustRole === 'trustee/grantor') || (this.trustRole === 'trustee');
      },
    },
    showAdditionalInfo: {
      get() {
        return ((this.trustRole === 'grantor' && this.isGrantorLiving === 'Yes') || (this.trustRole === 'trustee/grantor') || (this.trustRole === 'trustee'));
      },
    },
    showAdditionalQuestion: {
      get() {
        return (this.trustRole === 'grantor' && this.isGrantorLiving === 'Yes') || (this.trustRole === 'trustee/grantor') || (this.trustRole === 'trustee');
      },
    },
    isTrustee: {
      get() {
        return (this.trustRole === 'trustee/grantor') || (this.trustRole === 'trustee');
      },
    },
    showEmploymentInformation: {
      get() {
        return (this.employmentStatus === 'E' || this.employmentStatus === 'S' || this.employmentStatus === 'Z') && (this.trustRole === 'trustee' || this.trustRole === 'trustee/grantor');
      },
    },
    showAssociations: {
      get() {
        return this.employmentStatus !== '' && (this.trustRole === 'trustee' || this.trustRole === 'trustee/grantor');
      },
    },
    showAssociationsInput: {
      get() {
        return this.hasAssociation === 'Yes';
      },
    },
    showTradingSymbolInput: {
      get() {
        return this.hasControlPersonRelationship === 'Yes';
      },
    },
    showCompanyAddress: {
      get() {
        const show = (this.employmentStatus === 'E' || this.employmentStatus === 'Z') || (this.employmentStatus === 'S' && this.isSECompanyAddressSame === 'No');
        if (show) {
          this.initGoogleAutocomplete('employmentAddress-addressLine1', this.trustFillAddressValues.bind(null, 'employmentAddress', this.addressChanged.bind(this, 'employer')));
        }
        return show;
      },
    },
    showCountrySelect: {
      get() {
        return (this.trustRole === 'grantor' && this.isGrantorLiving === 'Yes') || (this.trustRole === 'trustee/grantor') || (this.trustRole === 'trustee');
      },
    },
    countryList: {
      get() {
        return JSON.stringify(this.getCountryList());
      },
    },

    dobMonth() {
      return this.dateOfBirth ? this.dateOfBirth.split('/')[0] : '';
    },
    dobDay() {
      return this.dateOfBirth ? this.dateOfBirth.split('/')[1] : '';
    },
    dobYear() {
      return this.dateOfBirth ? this.dateOfBirth.split('/')[2] : '';
    },
    usersRole() {
      return this.pu_additionalDetails.role;
    },
    hasMaxTrustees() {
      const userIsTrustee = ((this.pu_additionalDetails.role === 'trustee') || (this.pu_additionalDetails.role === 'trustee/grantor')) ? 1 : 0;
      const selectedTrustee = (this.selectedRole === 'trustee' || this.selectedRole === 'trustee/grantor');
      return ((this.additionalRoles.filter((person) => (person.role === 'trustee') || (person.role === 'trustee/grantor')).length) + userIsTrustee >= this.MAX_TRUSTEES) && selectedTrustee;
    },
    hasMaxGrantors() {
      const userIsGrantor = ((this.pu_additionalDetails.role === 'grantor') || (this.pu_additionalDetails.role === 'trustee/grantor')) ? 1 : 0;
      const selectedGrantor = (this.selectedRole === 'grantor' || this.selectedRole === 'trustee/grantor');
      return ((this.additionalRoles.filter((person) => (person.role === 'grantor') || (person.role === 'trustee/grantor')).length + userIsGrantor) >= this.MAX_GRANTORS) && selectedGrantor;
    },
    hasMaxAppointRemove() {
      if (this.pu_additionalDetails) {
        const applicantHasAppointRemove = this.pu_additionalDetails.hasAppointRemove ? 1 : 0;
        return this.additionalRoles.filter((person) => person.hasAppointRemove).length
          + applicantHasAppointRemove >= this.MAX_APPOINT_REMOVE;
      }

      return false;
    },
    hasMaxAmendRevoke() {
      if (this.pu_additionalDetails) {
        const applicantHasAmendRevoke = this.pu_additionalDetails.hasAmendRevoke ? 1 : 0;
        return this.additionalRoles.filter((person) => person.hasAmendRevoke).length
          + applicantHasAmendRevoke >= this.MAX_AMEND_REVOKE;
      }

      return false;
    },
    isManagedAccount() {
      return this.isManaged(this.routeId);
    },

    trustedRiskDone() {
      return false;
      // rollback the new feature where we disable fields of user PI for whom risk check was done previously
      // const { currentUserIndex, futureDestinationState } = this.getInstanceData().currentState;
      // if (futureDestinationState === 'trust-review-and-confirm') return false;
      // const { formData } = this.getInstanceData();
      // let userData = {};
      // if (Object.hasOwn(this.getInstanceData().currentState, 'currentUserIndex')
      // && Number.isInteger(this.getInstanceData().currentState.currentUserIndex)) {
      //   userData = formData.additionalUsers[currentUserIndex];
      // }
      // return userData?.riskData?.riskApiRiskLevel === 'ELEVATE' || userData?.riskData?.riskApiRiskLevel === 'ALLOW';
    },
    isOnlyGrantor() {
      const { currentUserIndex } = this.getInstanceData().currentState;
      const { formData } = this.getInstanceData();
      let userData = {};
      if (Object.hasOwn(this.getInstanceData().currentState, 'currentUserIndex') && Number.isInteger(this.getInstanceData().currentState.currentUserIndex)) {
        userData = formData.additionalUsers[currentUserIndex];
      }
      return userData?.role === 'grantor';
    },
  },
  mounted() {
    this.$store.commit('currentState/updateField', { path: 'stateName', value: 'trust-additional-role' });
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    this.removeModalClassIfExists();
    document.title = this.setTitle(this.pageTitle);
    this.applicant = this.getInstanceData().formData.primaryUser;
    this.trust = this.getInstanceData().formData.accountDetails;
    this.additionalRoles = this.getInstanceData().formData.additionalUsers;
    this.futureDestinationState = this.getInstanceData().currentState?.futureDestinationState;
    this.isLoading = false;
    const instanceData = this.getInstanceData();
    this.localEditMode = instanceData.currentState.editMode;
    let userIndex;
    if (instanceData && instanceData.currentState) {
      userIndex = instanceData.currentState.userIndex;
    }
    this.$store.commit('formData/updateField', { path: 'removeUserIndex', value: true });
    this.$store.commit('currentState/updateField', { path: 'removeUserIndex', value: true });
    if (typeof userIndex !== 'undefined' && Number.isInteger(userIndex)) {
      this.additionalUserIndex = userIndex;
      this.currentUser = { ...instanceData.formData.additionalUsers[this.additionalUserIndex] };
      this.primaryButtonLabel = instanceData.currentState.editMode ? 'Save' : 'Next';
      this.secondaryButtonLabel = instanceData.currentState.editMode ? 'Cancel' : 'Previous';
      this.$store.commit('currentState/updateField', { path: 'userIndex', value: '' });
      // Repopulate form for current user
      if (this.currentUser.firstName) { this.firstName = this.currentUser.firstName; }
      if (this.currentUser.middleName) { this.middleName = this.currentUser.middleName; }
      if (this.currentUser.lastName) { this.lastName = this.currentUser.lastName; }
      if (this.currentUser.hasAppointRemove) { this.hasAppointRemove = true; }

      if (this.currentUser) {
        this.amendValue = !!this.currentUser.hasAmend;
        this.revokeValue = !!this.currentUser.hasRevoke;
      }

      if (this.currentUser.addresses) {
        if (this.currentUser.addresses.mailing) { this.mailingAddress = this.currentUser.addresses.mailing; }
        if (this.currentUser.addresses.legal) { this.legalAddress = this.currentUser.addresses.legal; }
        if (this.currentUser.addresses.employer) { this.employerAddress = this.currentUser.addresses.employer; }
      }
      if (this.currentUser.dob) { this.dateOfBirth = this.currentUser.dob; }
      if (this.currentUser.dayPhone) { this.phoneNumber = this.currentUser.dayPhone; }
      if (this.currentUser.email) { this.email = this.currentUser.email; }
      if (this.currentUser.ssn) { this.ssn = this.currentUser.ssn; }
      if (this.currentUser.residenceCountry) { this.residenceCountry = this.currentUser.residenceCountry; }
      if (this.currentUser.isDeceased) { this.isGrantorLiving = this.currentUser.isDeceased === 'Yes' ? 'No' : 'Yes'; }
      if (this.currentUser.role) { this.trustRole = this.currentUser.role; }

      if (this.currentUser.employmentStatus) { this.employmentStatus = this.currentUser.employmentStatus; }
      if (this.currentUser.occupation) { this.occupation = this.currentUser.occupation; }
      if (this.currentUser.employersName) { this.company = this.currentUser.employersName; }
      if (this.currentUser.association) { this.association = this.currentUser.association; }
      if (this.currentUser.tradingSymbol) { this.tradingSymbol = this.currentUser.tradingSymbol; }
      if (this.currentUser.hasAssociation) { this.hasAssociation = this.currentUser.hasAssociation; }
      if (this.currentUser.hasControlPersonRelationship) {
        this.hasControlPersonRelationship = this.currentUser.hasControlPersonRelationship;
      }

      if (typeof this.currentUser.mobilePhoneCheckIndicator === 'boolean') { this.mobilePhoneCheckIndicator = this.currentUser.mobilePhoneCheckIndicator; }
      if (typeof this.currentUser.isMailingSameAsLegal === 'boolean') { this.isMailingSameAsLegal = this.currentUser.isMailingSameAsLegal; }
      if (typeof this.currentUser.isEmployerSameAsLegal === 'boolean') { this.isSECompanyAddressSame = this.currentUser.isEmployerSameAsLegal ? 'Yes' : 'No'; }
    } else {
      this.additionalUserIndex = instanceData.formData.additionalUsers.length;
      this.commitAdditionalUserData(false, 'hasAmendRevoke', this.additionalUserIndex);
      this.commitAdditionalUserData(false, 'hasAppointRemove', this.additionalUserIndex);
      this.commitAdditionalUserData(true, 'isMailingSameAsLegal', this.additionalUserIndex);
    }
  },

  methods: {
    nextButtonClicked(navAction) {
      this.isLoading = true;
      this.performPageValidation(navAction).then((valid) => {
        if (
          valid
          && this.roleValid
          && this.amendRevokeValid
          && this.appointRemoveValid
          && !this.ssnMatchesExistingSSN
          && !this.emailMatchesExistingTrusteesEmail
        ) {
          this.performNavigation(this.pd_content.primary_button.value);
        } else {
          this.isLoading = false;
        }
      });
    },
    cancelButtonClicked() {
      if (this.currentUser) {
        // Edit - we need to delete the changes but not the user
        const userReset = [...this.getInstanceData().formData.additionalUsers];
        userReset[this.additionalUserIndex] = this.currentUser;
        this.setListInstanceData('formData', userReset, 'additionalUsers');
      } else {
        // New - we need to delete the user entirely
        this.setListInstanceData('formData', this.getInstanceData().formData.additionalUsers.slice(0, -1), 'additionalUsers');
      }
    },
    selectedCitizenshipValue() {
      const { currentUserIndex, editMode } = this.getInstanceData().currentState;
      const { formData } = this.getInstanceData();
      let userData = {};
      if (Object.hasOwn(this.getInstanceData().currentState, 'currentUserIndex') && Number.isInteger(this.getInstanceData().currentState.currentUserIndex)) {
        userData = formData.additionalUsers[currentUserIndex];
      }
      return editMode ? `${this.pd_countryList.validCitizenship.find((element) => element.value === userData.residenceCountry)?.option ?? ''}` : '';
    },
    primaryButtonClick(navAction) {
      this.primaryButtonLoading = true;
      this.$store.commit('currentState/updateField', { path: 'editMode', value: false });
      this.$store.commit('currentState/updateField', { path: 'removeUserIndex', value: false });
      this.performPageValidation(navAction).then((valid) => {
        if (valid) {
          this.performNavigation('grantorOnlyOrEditModeNext');
        }
      });
    },
    secondaryButtonClick() {
      this.secondaryButtonLoading = true;
      const { editMode } = this.getInstanceData().currentState;
      const userReset = [...this.getInstanceData().formData.additionalUsers];
      if (editMode) {
        userReset[this.additionalUserIndex] = this.currentUser;
        this.setListInstanceData('formData', userReset, 'additionalUsers');
      } else {
        this.setListInstanceData('formData', userReset.slice(0, -1), 'additionalUsers');
      }
      this.$store.commit('currentState/updateField', { path: 'removeUserIndex', value: false });
      this.$store.commit('currentState/updateField', { path: 'editMode', value: false });
      this.$store.commit('currentState/updateField', { path: 'navAction', value: 'grantorOnlyOrEditModeNext' });
    },
    employmentStatusChanged(event) {
      this.employmentStatus = event.target.value;

      if (this.employmentStatus === 'S') {
        this.commitAdditionalUserData(true, 'isEmployerSameAsLegal', this.additionalUserIndex);
      } else {
        this.commitAdditionalUserData(false, 'isEmployerSameAsLegal', this.additionalUserIndex);
      }
      this.isSECompanyAddressSame = 'No';
      this.commitAdditionalUserData(this.employmentStatus, 'employmentStatus', this.additionalUserIndex);
    },
    hasAssociationChanged(toggleValue) {
      this.hasAssociation = toggleValue;

      this.commitAdditionalUserData(this.hasAssociation, 'hasAssociation', this.additionalUserIndex);
    },
    hasControlPersonRelationshipChanged(toggleValue) {
      this.hasControlPersonRelationship = toggleValue;

      this.commitAdditionalUserData(this.hasControlPersonRelationship, 'hasControlPersonRelationship', this.additionalUserIndex);
    },
    updateGrantorLiving(toggleValue) {
      this.isGrantorLiving = toggleValue;
      this.commitAdditionalUserData((toggleValue === 'Yes' ? 'No' : 'Yes'), 'isDeceased', this.additionalUserIndex);
    },
    forceRerender() {
      this.rerenderKey += 1;
    },
    trusteeClicked() {
      this.isGrantorLiving = undefined;
      this.selectedRole = 'trustee';

      this.commitAdditionalUserData(undefined, 'isDeceased', this.additionalUserIndex);
      this.commitAdditionalUserData('trustee', 'role', this.additionalUserIndex);

      this.resetIndividualRoleFields();
    },
    grantorClicked() {
      this.selectedRole = 'grantor';

      this.commitAdditionalUserData('grantor', 'role', this.additionalUserIndex);

      this.resetIndividualRoleFields();
    },
    trusteeGrantorClicked() {
      this.isGrantorLiving = undefined;
      this.selectedRole = 'trustee/grantor';

      this.commitAdditionalUserData(undefined, 'isDeceased', this.additionalUserIndex);
      this.commitAdditionalUserData('trustee/grantor', 'role', this.additionalUserIndex);

      this.resetIndividualRoleFields();
    },
    isSECompanyAddressSameChanged(toggleValue) {
      this.isSECompanyAddressSame = toggleValue;
      this.commitAdditionalUserData((toggleValue === 'Yes'), 'isEmployerSameAsLegal', this.additionalUserIndex);
    },
    fullNameChanged(event) {
      const index = this.additionalUserIndex;
      this.firstName = event.detail.fullname.firstName;
      this.middleName = event.detail.fullname.middleName;
      this.lastName = event.detail.fullname.lastName;
      this.commitAdditionalUserData(this.firstName, 'firstName', index);
      this.commitAdditionalUserData(this.middleName, 'middleName', index);
      this.commitAdditionalUserData(this.lastName, 'lastName', index);
    },
    ssnChanged(event) {
      this.ssn = event.detail.value;
      this.commitAdditionalUserData(this.ssn, 'ssn', this.additionalUserIndex);
    },
    dateOfBirthChanged(event) {
      this.dateOfBirth = `${event.detail.month}/${event.detail.day}/${event.detail.year}`;
      this.commitAdditionalUserData(this.dateOfBirth, 'dob', this.additionalUserIndex);
    },
    phoneNumberChanged(event) {
      const index = this.additionalUserIndex;
      this.phoneNumber = event.detail.telephoneNumber;
      this.commitAdditionalUserData(this.phoneNumber, 'dayPhone', index);
      this.commitAdditionalUserData(true, 'mobilePhoneCheckIndicator', index);
    },
    emailChanged(event) {
      this.email = event.detail.value;
      this.commitAdditionalUserData(this.email, 'email', this.additionalUserIndex);
    },
    residenceCountryChanged(event) {
      this.residenceCountry = event.detail.selectValue;
      this.commitAdditionalUserData(this.residenceCountry, 'residenceCountry', this.additionalUserIndex);
    },
    addressChanged(addressType, event) {
      const index = this.additionalUserIndex;

      this[`${addressType}Address`] = {
        line1: event.detail.address.addressLine1,
        line2: event.detail.address.addressLine2,
        city: event.detail.address.cityName,
        state: event.detail.address.stateCode,
        zip: event.detail.address.zipCode,
      };

      const addresses = {
        ...(this.getInstanceData().formData.additionalUsers[this.additionalUserIndex].addresses || {}),
        [addressType]: {
          line1: event.detail.address.addressLine1,
          line2: event.detail.address.addressLine2,
          city: event.detail.address.cityName,
          state: event.detail.address.stateCode,
          zip: event.detail.address.zipCode,
        },
      };

      this.commitAdditionalUserData(addresses, 'addresses', index);
    },
    occupationChanged(event) {
      this.commitAdditionalUserData(event.target.value, 'occupation', this.additionalUserIndex);
    },
    companyNameChanged(event) {
      this.commitAdditionalUserData(event.target.value, 'employersName', this.additionalUserIndex);
    },
    associationChanged(event) {
      this.commitAdditionalUserData(event.target.value, 'association', this.additionalUserIndex);
    },
    tradingSymbolChanged(event) {
      this.commitAdditionalUserData(event.target.value, 'tradingSymbol', this.additionalUserIndex);
    },
    resetIndividualRoleFields() {
      // Reset SSN
      this.ssn = '';
      this.commitAdditionalUserData(undefined, 'ssn', this.additionalUserIndex);

      // Reset Date of Birth
      this.dateOfBirth = '';
      this.commitAdditionalUserData(undefined, 'dob', this.additionalUserIndex);

      // Reset Phone Number
      this.phoneNumber = '';
      this.commitAdditionalUserData(undefined, 'dayPhone', this.additionalUserIndex);

      // Reset Email
      this.email = '';
      this.commitAdditionalUserData(undefined, 'email', this.additionalUserIndex);

      // Reset Residence Country
      this.residenceCountry = '';
      this.commitAdditionalUserData(undefined, 'residenceCountry', this.additionalUserIndex);

      // Reset Mobile Phone Indicator
      this.mobilePhoneCheckIndicator = true;
      this.commitAdditionalUserData(undefined, 'mobilePhoneCheckIndicator', this.additionalUserIndex);

      // Reset Employment Status
      this.employmentStatus = '';
      this.commitAdditionalUserData(undefined, 'employmentStatus', this.additionalUserIndex);

      // Reset Cccupation
      this.occupation = '';
      this.commitAdditionalUserData(undefined, 'occupation', this.additionalUserIndex);

      // Reset Company
      this.company = '';
      this.commitAdditionalUserData(undefined, 'employersName', this.additionalUserIndex);

      // Reset Association
      this.association = '';
      this.hasAssociation = '';
      this.commitAdditionalUserData(undefined, 'association', this.additionalUserIndex);

      // Reset Trading Symbol
      this.tradingSymbol = '';
      this.hasControlPersonRelationship = '';
      this.commitAdditionalUserData(undefined, 'tradingSymbol', this.additionalUserIndex);

      // Reset Employer Same As Legal Flag
      this.isSECompanyAddressSame = 'Yes';
      this.commitAdditionalUserData(undefined, 'isEmployerSameAsLegal', this.additionalUserIndex);

      // Reset Mailing Same As Legal Flag
      this.isMailingSameAsLegal = true;
      this.commitAdditionalUserData(true, 'isMailingSameAsLegal', this.additionalUserIndex);

      // Reset Addresses
      this.mailingAddress = {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      };

      this.legalAddress = {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      };

      this.employerAddress = {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      };

      this.commitAdditionalUserData({}, 'addresses', this.additionalUserIndex);

      this.isGrantorLiving = '';

      // Finally, because Vue doesn't think some of the components changed
      this.forceRerender();
    },
  },
};

</script>

<style scoped>
  .top-margin-zero {
    margin-top: 0;
  }

  .dob-coc {
    font-size: 0.75em;
    font-weight: 700;
  }

  .dob-margin {
    margin-bottom: 20px;
  }
  .mt-1{
    margin-top: 1rem;
  }
  .mt-1-5{
    margin-top: 1.5rem;
  }
</style>
